import React, { FC, useRef, useEffect } from 'react';
import { ActionButton } from '@src/components/ActionButton';
import { CRMTemplateView, UserView, ProductsLocalizationMapping } from 'common';
import dayjs from 'dayjs';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { CRMTemplateType } from 'common/dist/types/models/CRMTemplate';
import { ADALONG_COLORS } from '@src/styles';
import { style } from 'typestyle';
import { TableFilter } from '@src/components/TableFilter';
import { Loader } from '@src/components/Loader';

export type InlineTemplate = {
  market: string;
  language: string;
  type: CRMTemplateType;
  hashtag: string;
  name: string;
  url: string;
  content: string;
  terms: string;
};

interface TemplateTableProps {
  templates: CRMTemplateView[];
  loading: boolean;
  showInlineEditor: boolean;
  inlineValues: InlineTemplate;
  urlError: boolean;
  termsError: boolean;
  expandedContentIdx: number | null;
  savingCreateTemplate: boolean;
  user: UserView | null | undefined;
  userConfig?: any;
  validationErrors: {
    name?: boolean;
    content?: boolean;
    hashtag?: boolean;
  };
  onInlineChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  onInlineSave: () => void;
  onInlineCancel: () => void;
  onEdit: (template: CRMTemplateView) => void;
  onDelete: (template: CRMTemplateView) => void;
  onDuplicate: (template: CRMTemplateView) => void;
  onContentToggle: (idx: number | null) => void;
  onInsertCreator?: () => void;
  onInsertTerms?: () => void;
  onInsertHashtag?: () => void;
  onInsertUrl?: () => void;
  // Filter and sort props
  getFilterOptions: (field: string) => Array<{ value: string; label: string; selected: boolean; }>;
  onFilterChange: (field: string, selected: string[]) => void;
  onSortChange: (sortBy: string, direction: 'asc' | 'desc') => void;
  currentSortBy: string;
  currentSortDirection: 'asc' | 'desc';
  productCountryMappings?: ProductsLocalizationMapping[];
}

const TemplateTable: FC<TemplateTableProps> = ({
  templates,
  loading,
  showInlineEditor,
  inlineValues,
  urlError,
  termsError,
  expandedContentIdx,
  savingCreateTemplate,
  user,
  userConfig,
  validationErrors,
  onInlineChange,
  onInlineSave,
  onInlineCancel,
  onEdit,
  onDelete,
  onDuplicate,
  onContentToggle,
  onInsertCreator,
  onInsertTerms,
  onInsertHashtag,
  onInsertUrl,
  getFilterOptions,
  onFilterChange,
  onSortChange,
  currentSortBy,
  currentSortDirection,
  productCountryMappings,
}) => {
  const showMarket = !!(productCountryMappings && productCountryMappings.length);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea
  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onInlineChange(e);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`;
    }
  };

  // Calculate max character count based on type
  const getMaxCharCount = () => {
    if (inlineValues.type === 'RR') {
      return userConfig?.config?.rightstemplate?.maxComment || 1000;
    }
    return 500; // DM type default
  };

  const maxCharCount = getMaxCharCount();
  const currentCharCount = inlineValues.content.length;
  const isOverLimit = currentCharCount > maxCharCount;

  // Initial resize when content changes
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`;
    }
  }, [inlineValues.content, showInlineEditor]);

  return (
    <div style={{ width: '100%', overflowX: 'hidden' }}>
      <table className={tableClass}>
        <colgroup>
          <col style={{ width: '7%' }} />
          <col style={{ width: '13%' }} />
          <col style={{ width: '7%' }} />
          <col style={{ width: '7%' }} />
          <col style={{ width: '5%' }} />
          <col style={{ width: '9%' }} />
          <col style={{ width: '10%' }} />
          <col style={{ width: '10%' }} />
          <col style={{ width: '10%' }} />
          <col style={{ width: '16%' }} />
          <col style={{ width: '6%' }} />
        </colgroup>
      <thead>
        <tr className={headerRowClass}>
          <th className={thClass}>
            <div className={headerWithFilterClass}>
              Date
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {currentSortBy === 'date' ? (
                  currentSortDirection === 'asc' ? 
                    <span className={sortIconClass} onClick={() => onSortChange('date', 'desc')}>↑</span> :
                    <span className={sortIconClass} onClick={() => onSortChange('date', 'asc')}>↓</span>
                ) : null }
                {currentSortBy !== 'date' ? (
                  <span style={{ fontSize: '14px', color: ADALONG_COLORS.LIGHT_GRAY, cursor: 'pointer' }} onClick={() => onSortChange('date', 'desc')}>↕</span>
                ) : null}
              </div>
            </div>
          </th>
          <th className={thClass}>
            <div className={headerWithFilterClass}>
              Owner
              <TableFilter
                type="filter"
                options={getFilterOptions('owner')}
                onFilterChange={(selected) => onFilterChange('owner', selected)}
              />
            </div>
          </th>
          {showMarket ? (
            <th className={thClass}>
              <div className={headerWithFilterClass}>
                Market
                <TableFilter
                  type="filter"
                  options={getFilterOptions('market')}
                  onFilterChange={(selected) => onFilterChange('market', selected)}
                />
              </div>
            </th>
          ) : null}
          <th className={thClass}>
            <div className={headerWithFilterClass}>
              Language
              <TableFilter
                type="filter"
                options={getFilterOptions('language')}
                onFilterChange={(selected) => onFilterChange('language', selected)}
              />
            </div>
          </th>
          <th className={thClass}>
            <div className={headerWithFilterClass}>
              Type
              <TableFilter
                type="filter"
                options={getFilterOptions('type')}
                onFilterChange={(selected) => onFilterChange('type', selected)}
              />
            </div>
          </th>
          <th className={thClass}>
            <div className={headerWithFilterClass}>
              Hashtag
              <TableFilter
                type="filter"
                options={getFilterOptions('hashtag')}
                onFilterChange={(selected) => onFilterChange('hashtag', selected)}
              />
            </div>
          </th>
          <th className={thClass}>
            <div className={headerWithFilterClass}>
              Campaign
              <TableFilter
                type="filter"
                options={getFilterOptions('campaign')}
                onFilterChange={(selected) => onFilterChange('campaign', selected)}
              />
            </div>
          </th>
          <th className={thClass}>
            <div className={headerWithFilterClass}>
              URL
              <TableFilter
                type="filter"
                options={getFilterOptions('url')}
                onFilterChange={(selected) => onFilterChange('url', selected)}
              />
            </div>
          </th>
          <th className={thClass}>
            <div className={headerWithFilterClass}>
              Terms
              <TableFilter
                type="filter"
                options={getFilterOptions('terms')}
                onFilterChange={(selected) => onFilterChange('terms', selected)}
              />
            </div>
          </th>
          <th className={thClass}>Content</th>
          <th className={iconColClass}></th>
        </tr>
      </thead>
      <tbody>
        {showInlineEditor ? (
          <tr className={rowClass}>
            <td className={tdDateClass}>
              {dayjs().format('DD/MM/YY')}
            </td>
            <td className={tdEllipsisClass}>
              {user?.email || user?._id || ''}
            </td>
            <td className={tdEllipsisClass}>
              <select
                name="market"
                value={inlineValues.market}
                onChange={onInlineChange}
                className={inputClass}
              >
                <option value="">Select market</option>
                {productCountryMappings && productCountryMappings.length ? (
                  productCountryMappings.map((m) => (
                    <option key={m.name} value={m.name}>{m.name}</option>
                  ))
                ) : (
                  getFilterOptions('market').map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))
                )}
              </select>
            </td>
            <td className={tdEllipsisClass}>
              <input 
                name="language" 
                value={inlineValues.language} 
                onChange={onInlineChange} 
                placeholder="Language"
                className={inputClass} 
              />
            </td>
            <td className={tdEllipsisClass}>
              <select name="type" value={inlineValues.type} onChange={onInlineChange} className={inputClass}>
                <option value="DM">DM</option>
                <option value="RR">RR</option>
              </select>
            </td>
            <td className={tdEllipsisClass}>
              {inlineValues.type === 'RR' ? (
                <div>
                  <input 
                    name="hashtag" 
                    value={inlineValues.hashtag} 
                    onChange={onInlineChange} 
                    placeholder="#hashtag*" 
                    className={validationErrors.hashtag ? inputErrorClass : inputClass} 
                  />
                  {validationErrors.hashtag ? (
                    <div className={validationErrorClass}>This field is mandatory</div>
                  ) : null}
                </div>
              ) : (
                <span className={naTextClass}>N/A</span>
              )}
            </td>
            <td className={tdEllipsisClass}>
              <div>
                <input 
                  name="name" 
                  value={inlineValues.name} 
                  onChange={onInlineChange} 
                  placeholder="Campaign*"
                  className={validationErrors.name ? inputErrorClass : inputClass} 
                />
                {validationErrors.name ? (
                  <div className={validationErrorClass}>This field is mandatory</div>
                ) : null}
              </div>
            </td>
            <td className={tdEllipsisClass}>
              <input 
                name="url" 
                value={inlineValues.url} 
                onChange={onInlineChange} 
                placeholder="URL"
                className={urlError ? inputErrorClass : inputClass} 
              />
              {urlError ? (
                <div className={urlErrorClass}>Invalid URL</div>
              ) : null}
            </td>
            <td className={tdEllipsisClass}>
              <input 
                name="terms" 
                value={inlineValues.terms} 
                onChange={onInlineChange} 
                placeholder="Terms URL"
                className={termsError ? inputErrorClass : inputClass} 
              />
              {termsError ? (
                <div className={urlErrorClass}>Invalid Terms URL</div>
              ) : null}
            </td>
            <td style={{ padding: '8px', verticalAlign: 'top' }}>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '6px', flexWrap: 'wrap' }}>
                {onInsertCreator ? (
                  <ActionButton
                    variant="transparent"
                    size="small"
                    onAction={onInsertCreator}
                    style={{ minWidth: 0 }}
                  >
                    Creator
                  </ActionButton>
                ) : null}
                {onInsertTerms && inlineValues.terms && inlineValues.terms.trim() ? (
                  <ActionButton
                    variant="transparent"
                    size="small"
                    onAction={onInsertTerms}
                    style={{ minWidth: 0 }}
                  >
                    Terms
                  </ActionButton>
                ) : null}
                {inlineValues.url && inlineValues.url.trim() && onInsertUrl ? (
                  <ActionButton
                    variant="transparent"
                    size="small"
                    onAction={onInsertUrl}
                    style={{ minWidth: 0 }}
                  >
                    URL
                  </ActionButton>
                ) : null}
                {inlineValues.type === 'RR' && inlineValues.hashtag && inlineValues.hashtag.trim() && onInsertHashtag ? (
                  <ActionButton
                    variant="transparent"
                    size="small"
                    onAction={onInsertHashtag}
                    style={{ minWidth: 0 }}
                  >
                    Hashtag
                  </ActionButton>
                ) : null}
              </div>
              <div>
                <textarea 
                  ref={textareaRef}
                  name="content" 
                  value={inlineValues.content} 
                  onChange={handleTextareaChange} 
                  placeholder="Content*"
                  className={validationErrors.content ? textareaErrorClass : textareaClass}
                  rows={3}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
                  {validationErrors.content ? (
                    <div className={validationErrorClass}>This field is mandatory</div>
                  ) : null}
                  <div className={isOverLimit ? charCountErrorClass : charCountClass}>
                    {currentCharCount}/{maxCharCount}
                  </div>
                </div>
              </div>
            </td>
            <td className={iconColClass}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                <ActionButton 
                  size="small" 
                  variant="blue" 
                  onAction={onInlineSave} 
                  disabled={savingCreateTemplate || urlError} 
                >
                  Save
                </ActionButton>
                <ActionButton size="small" variant="gray" onAction={onInlineCancel}>
                  Cancel
                </ActionButton>
              </div>
            </td>
          </tr>
        ) : null }
        {loading ? (
          <tr>
            <td colSpan={11} className={centerGrayClass}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <Loader size={20} />
                <span>Loading templates...</span>
              </div>
            </td>
          </tr>
        ) : templates.length === 0 && !showInlineEditor ? (
          <tr><td colSpan={11} className={centerGrayClass}>No templates</td></tr>
        ) : (
          templates.map((tpl, idx) => {
            const isExpanded = expandedContentIdx === idx;
            return (
              <tr key={idx} className={rowClass}>
                <td className={tdDateClass}>
                  {tpl.createdAt ? dayjs(tpl.createdAt).format('DD/MM/YY') : ''}
                </td>
                <td className={tdEllipsisClass}>
                  {typeof tpl.owner === 'object' && tpl.owner !== null 
                    ? tpl.owner.email || tpl.owner._id 
                    : tpl.owner}
                </td>
                <td className={tdEllipsisClass}>{tpl.market}</td>
                <td className={tdEllipsisClass}>{tpl.language}</td>
                <td className={tdEllipsisClass}>{tpl.type}</td>
                <td className={tdEllipsisClass}>
                  {tpl.type === 'RR' ? tpl.hashtag : <span className={naTextClass}>N/A</span>}
                </td>
                <td className={tdEllipsisClass}>{tpl.name}</td>
                <td className={tdEllipsisClass}>{tpl.url}</td>
                <td className={tdEllipsisClass}>{tpl.terms}</td>
                <td className={tdContentClass}>
                  <span
                    className={isExpanded ? contentSpanExpandedClass : contentSpanClass}
                    title={tpl.content}
                    onClick={() => onContentToggle(isExpanded ? null : idx)}
                  >
                    {tpl.content}
                    {!isExpanded && tpl.content && tpl.content.length > 40 ? (
                      <span className={moreTextClass}>... more</span>
                    ) : null}
                  </span>
                </td>
                <td className={iconColClass}>
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '2px' }}>
                    <span className={iconBtnClass} title="Copy" onClick={() => onDuplicate(tpl)}>
                      <ContentCopyIcon fontSize="small" />
                    </span>
                    <span className={iconBtnClass} title="Edit" onClick={() => onEdit(tpl)}>
                      <EditIcon fontSize="small" />
                    </span>
                    <span className={iconBtnClass} title="Delete" onClick={() => onDelete(tpl)}>
                      <DeleteIcon fontSize="small" />
                    </span>
                  </div>
                </td>
              </tr>
            );
          })
        )}
      </tbody>
    </table>
    </div>
  );
};

const sortIconClass = style({ fontSize: '12px', cursor: 'pointer' });

const tableClass = style({
  width: '100%',
  borderCollapse: 'collapse',
  marginTop: 16,
  marginBottom: 16,
  fontSize: 14,
  background: 'transparent',
  color: 'white',
  tableLayout: 'fixed',
});

const headerRowClass = style({
  borderBottom: '2px solid #444',
  background: 'transparent',
});

const thClass = style({
  fontWeight: 600,
  fontSize: 15,
  color: 'white',
  textAlign: 'left',
  padding: '14px 0 14px 0',
  background: 'transparent',
  position: 'relative',
});

const headerWithFilterClass = style({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  minHeight: 20,
});

const rowClass = style({
  borderBottom: '1px solid #333',
  background: 'transparent',
  transition: 'background 0.15s',
  height: 54,
});

const tdDateClass = style({
  fontVariantNumeric: 'tabular-nums',
  fontWeight: 500,
  color: 'white',
  padding: '8px',
  minWidth: 65,
  maxWidth: 75,
  fontSize: 12,
  whiteSpace: 'nowrap',
  height: 54,
  verticalAlign: 'middle',
});

const tdEllipsisClass = style({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  color: 'white',
  padding: '0 8px',
  fontSize: 14,
  verticalAlign: 'middle',
  height: 54,
});

const tdContentClass = style({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  color: 'white',
  padding: '0 8px',
  fontSize: 14,
  verticalAlign: 'middle',
  height: 54,
  cursor: 'pointer',
});

const iconColClass = style({
  width: '6%',
  minWidth: 60,
  textAlign: 'center',
  color: '#aaa',
  fontSize: 16,
  padding: '0 6px',
  whiteSpace: 'nowrap',
  height: 54,
  verticalAlign: 'middle',
});

const iconBtnClass = style({
  display: 'inline-block',
  margin: '0 4px',
  cursor: 'pointer',
  color: '#aaa',
  fontSize: 16,
  transition: 'color 0.15s',
  padding: '2px',
  borderRadius: '2px',
  $nest: {
    '&:hover': {
      color: '#fff',
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
});

const inputClass = style({
  width: '90%',
  background: ADALONG_COLORS.SMOOTH_BLACK,
  color: ADALONG_COLORS.SMOOTH_WHITE,
  border: `1px solid ${ADALONG_COLORS.DARKER_GRAY}`,
  borderRadius: 6,
  padding: '7px 10px',
  fontSize: 14,
  outline: 'none',
  margin: 0,
  boxSizing: 'border-box',
  transition: 'border 0.2s',
  minHeight: 36,
  verticalAlign: 'middle',
  fontFamily: 'inherit',
  resize: 'none',
});

const inputErrorClass = style({
  width: '90%',
  background: ADALONG_COLORS.SMOOTH_BLACK,
  color: ADALONG_COLORS.SMOOTH_WHITE,
  border: `1px solid ${ADALONG_COLORS.FLASH_RED}`,
  borderRadius: 6,
  padding: '7px 10px',
  fontSize: 14,
  outline: 'none',
  margin: 0,
  boxSizing: 'border-box',
  transition: 'border 0.2s',
  minHeight: 36,
  verticalAlign: 'middle',
  fontFamily: 'inherit',
  resize: 'none',
});

const textareaClass = style({
  width: '100%',
  minHeight: 80,
  maxHeight: 200,
  resize: 'vertical',
  background: ADALONG_COLORS.SMOOTH_BLACK,
  color: ADALONG_COLORS.SMOOTH_WHITE,
  border: `1px solid ${ADALONG_COLORS.DARKER_GRAY}`,
  borderRadius: 6,
  padding: '10px',
  fontSize: 14,
  outline: 'none',
  margin: 0,
  boxSizing: 'border-box',
  transition: 'border 0.2s',
  verticalAlign: 'top',
  fontFamily: 'inherit',
  lineHeight: '1.5',
  wordWrap: 'break-word',
  whiteSpace: 'pre-wrap',
  overflowWrap: 'break-word',
  $nest: {
    '&:focus': {
      borderColor: ADALONG_COLORS.LIGHT_GRAY,
    },
  },
});

const textareaErrorClass = style({
  width: '100%',
  minHeight: 80,
  maxHeight: 200,
  resize: 'vertical',
  background: ADALONG_COLORS.SMOOTH_BLACK,
  color: ADALONG_COLORS.SMOOTH_WHITE,
  border: `1px solid ${ADALONG_COLORS.FLASH_RED}`,
  borderRadius: 6,
  padding: '10px',
  fontSize: 14,
  outline: 'none',
  margin: 0,
  boxSizing: 'border-box',
  transition: 'border 0.2s',
  verticalAlign: 'top',
  fontFamily: 'inherit',
  lineHeight: '1.5',
  wordWrap: 'break-word',
  whiteSpace: 'pre-wrap',
  overflowWrap: 'break-word',
  $nest: {
    '&:focus': {
      borderColor: ADALONG_COLORS.FLASH_RED,
    },
  },
});

const urlErrorClass = style({
  color: ADALONG_COLORS.FLASH_RED,
  fontSize: 12,
  marginTop: 2,
});

const validationErrorClass = style({
  color: ADALONG_COLORS.FLASH_RED,
  fontSize: 12,
  marginTop: 2,
});

const charCountClass = style({
  fontSize: 12,
  color: ADALONG_COLORS.LIGHT_GRAY,
  marginLeft: 'auto',
});

const charCountErrorClass = style({
  fontSize: 12,
  color: ADALONG_COLORS.FLASH_RED,
  marginLeft: 'auto',
});

const centerGrayClass = style({
  textAlign: 'center',
  color: '#888',
});

const naTextClass = style({
  color: ADALONG_COLORS.LIGHT_GRAY,
});

const contentSpanClass = style({
  display: 'inline-block',
  width: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  verticalAlign: 'middle',
  color: '#fff',
  cursor: 'pointer',
  transition: 'all 0.15s',
});

const contentSpanExpandedClass = style({
  display: 'inline-block',
  width: '100%',
  overflow: 'visible',
  textOverflow: 'unset',
  whiteSpace: 'pre-wrap',
  verticalAlign: 'middle',
  color: '#fff',
  cursor: 'pointer',
  transition: 'all 0.15s',
  wordBreak: 'break-word',
});

const moreTextClass = style({
  color: ADALONG_COLORS.LIGHT_WHITE,
  marginLeft: 4,
});

export default TemplateTable;